/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  Badge,
  Chip,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
} from "@mui/material"
import {
  CloudOff,
  CloudDone,
  Sync,
  ReportProblem,
  CheckCircle,
  Warning,
  AccessTime,
  LocationOn,
  Person,
  Refresh,
  Public,
} from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"
import dayjs from "dayjs"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema SIAC Táctico (Rojo dominante para Alertas)
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

// Diccionario visual de alertas para el Coordinador
const MAPEO_ALERTAS = {
  DELEGADO_EXPULSADO: { label: "Expulsión de Delegado", color: "error" },
  FALTA_MATERIAL: { label: "Falta de Material", color: "secondary" },
  PROSELITISMO: { label: "Proselitismo Político", color: "secondary" },
  VIOLENCIA: { label: "Violencia Física", color: "error" },
  MESA_CERRADA: { label: "Mesa no instalada", color: "error" },
  FRAUDE_EVIDENTE: { label: "Intento de Fraude", color: "error" },
  OTRO: { label: "Emergencia General", color: "secondary" },
}

const AlertasSeguridadRecinto = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [activeTab, setActiveTab] = useState(0) // 0: Activas, 1: Resueltas

  // Estados de datos y seguridad RLS
  const [incidencias, setIncidencias] = useState([])
  const [colaResoluciones, setColaResoluciones] = useState([])
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // ========================================================
  // 1. INICIALIZACIÓN Y POLLING (AUTO-REFRESCO)
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarAlertas()
    verificarColaLocal()

    // Auto-refresco cada 60 segundos
    const interval = setInterval(() => {
      if (navigator.onLine) cargarAlertas(true)
    }, 60000)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      clearInterval(interval)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("resolucion_incidencias_queue")) || []
    setColaResoluciones(cola)
  }

  const cargarAlertas = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getAlertasCoordinador", {})

      if (res.data && res.data.success === false) {
        setErrorAcceso(res.data.message)
        return
      }

      const alertasReales = res.data.data || []
      const recintoAsignado = res.data.recinto || ""
      const isGlobalBackend = res.data.es_global || false

      setIncidencias(alertasReales)
      setRecintoActual(recintoAsignado)
      setEsGlobal(isGlobalBackend)

      await localforage.setItem("coord_alertas_cache", alertasReales)
      await localforage.setItem("coord_recinto_alertas", recintoAsignado)
      await localforage.setItem("coord_global_alertas", isGlobalBackend)
    } catch (error) {
      console.log("Cargando alertas desde caché offline...")
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = (await localforage.getItem("coord_alertas_cache")) || []
        const rCache = (await localforage.getItem("coord_recinto_alertas")) || ""
        const cacheGlobal = (await localforage.getItem("coord_global_alertas")) || false

        setIncidencias(cache)
        setRecintoActual(rCache)
        setEsGlobal(cacheGlobal)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // 2. LÓGICA PARA MARCAR COMO RESUELTA
  // ========================================================
  const handleResolverAlerta = async (inc_codigo) => {
    const confirmacion = await Swal.fire({
      title: "¿Marcar como Resuelta?",
      text: "Esto indica que el problema ha sido solucionado.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2E7D32",
      cancelButtonColor: "#d32f2f",
      confirmButtonText: "Sí, problema resuelto",
    })

    if (!confirmacion.isConfirmed) return

    const alertasActualizadas = incidencias.map((inc) =>
      inc.inc_codigo === inc_codigo ? { ...inc, inc_estado: "RESUELTA" } : inc,
    )
    setIncidencias(alertasActualizadas)
    await localforage.setItem("coord_alertas_cache", alertasActualizadas)

    const payload = {
      inc_codigo,
      nuevo_estado: "RESUELTA",
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      const cola = (await localforage.getItem("resolucion_incidencias_queue")) || []
      cola.push(payload)
      await localforage.setItem("resolucion_incidencias_queue", cola)
      setColaResoluciones(cola)

      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "info",
        title: "Resolución guardada offline",
        showConfirmButton: false,
        timer: 2500,
      })
    } else {
      try {
        await api.post("/ControlElectoral/resolverIncidencia", payload)
        Swal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: "Alerta cerrada en la central",
          showConfirmButton: false,
          timer: 2500,
        })
      } catch (error) {
        const cola = (await localforage.getItem("resolucion_incidencias_queue")) || []
        cola.push(payload)
        await localforage.setItem("resolucion_incidencias_queue", cola)
        setColaResoluciones(cola)
      }
    }
  }

  // ========================================================
  // 3. SINCRONIZACIÓN MANUAL DE RESOLUCIONES
  // ========================================================
  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Sin señal", "No hay red para sincronizar.", "warning")
    const cola = (await localforage.getItem("resolucion_incidencias_queue")) || []
    if (cola.length === 0) return

    setIsSyncing(true)
    let colaRestante = [...cola]

    for (let i = 0; i < cola.length; i++) {
      try {
        await api.post("/ControlElectoral/resolverIncidencia", cola[i])
        colaRestante = colaRestante.filter((c) => c.inc_codigo !== cola[i].inc_codigo)
      } catch (err) {
        console.error("Fallo al sincronizar resolución", err)
      }
    }

    await localforage.setItem("resolucion_incidencias_queue", colaRestante)
    setColaResoluciones(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("Sincronizado", "Todas las resoluciones llegaron a la base de datos.", "success")
    }
  }

  const alertasActivas = incidencias.filter((inc) => inc.inc_estado === "ACTIVA")
  const alertasResueltas = incidencias.filter((inc) => inc.inc_estado === "RESUELTA")
  const dataMostrada = activeTab === 0 ? alertasActivas : alertasResueltas

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? <CloudOff color="error" sx={{ mr: 1 }} /> : <CloudDone color="success" sx={{ mr: 1 }} />}
            <Typography variant="body2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "MODO OFFLINE ACTIVADO" : "CONEXIÓN ESTABLE"}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={() => cargarAlertas(false)}
              disabled={isLoading || isOffline}
              size="small"
              sx={{ border: "1px solid #ccc" }}
            >
              <Refresh fontSize="small" />
            </IconButton>
            <Badge badgeContent={colaResoluciones.length} color="secondary">
              <IconButton
                onClick={sincronizarCola}
                disabled={colaResoluciones.length === 0 || isOffline}
                sx={{ bgcolor: "#ffebee" }}
              >
                <Sync color={colaResoluciones.length > 0 ? "error" : "disabled"} />
              </IconButton>
            </Badge>
          </Box>
        </Box>

        <Typography variant="h5" color="error.main" fontWeight="bold" mb={1} display="flex" alignItems="center">
          <ReportProblem sx={{ mr: 1, fontSize: 32 }} /> Alertas de Seguridad
        </Typography>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ mt: 4, borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            {recintoActual && (
              <Box display="flex" alignItems="center" mb={2}>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  📍 Cobertura: {recintoActual}
                </Typography>
                {esGlobal && (
                  <Chip
                    icon={<Public fontSize="small" />}
                    label="MODO GLOBAL"
                    color="info"
                    size="small"
                    sx={{ ml: 2, fontWeight: "bold" }}
                  />
                )}
              </Box>
            )}

            <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                variant="fullWidth"
                indicatorColor={activeTab === 0 ? "error" : "success"}
                textColor="inherit"
              >
                <Tab
                  label={`S.O.S ACTIVOS (${alertasActivas.length})`}
                  sx={{ fontWeight: "bold", color: activeTab === 0 ? "#d32f2f" : "text.secondary" }}
                />
                <Tab
                  label={`RESUELTAS (${alertasResueltas.length})`}
                  sx={{ fontWeight: "bold", color: activeTab === 1 ? "#2E7D32" : "text.secondary" }}
                />
              </Tabs>
            </Paper>

            {isLoading ? (
              <Box display="flex" justifyContent="center" mt={5}>
                <CircularProgress color="error" />
              </Box>
            ) : dataMostrada.length === 0 ? (
              <Box textAlign="center" my={5} p={3}>
                <CheckCircle sx={{ fontSize: 60, color: "#a5d6a7", mb: 2 }} />
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  Todo en orden
                </Typography>
                <Typography color="textSecondary">
                  No hay reportes {activeTab === 0 ? "activos" : "resueltos"} en este momento.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2} pb={4}>
                {dataMostrada.map((inc) => {
                  const cfgVisual = MAPEO_ALERTAS[inc.inc_tipo] || MAPEO_ALERTAS.OTRO
                  const isActiva = inc.inc_estado === "ACTIVA"

                  return (
                    <Grid item xs={12} md={6} lg={4} key={inc.inc_codigo}>
                      <Card
                        elevation={isActiva ? 4 : 1}
                        sx={{
                          borderRadius: 3,
                          borderLeft: `8px solid ${isActiva ? "#d32f2f" : "#2E7D32"}`,
                          opacity: isActiva ? 1 : 0.8,
                        }}
                      >
                        <CardContent sx={{ p: "16px !important" }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Chip
                              icon={isActiva ? <Warning /> : <CheckCircle />}
                              label={cfgVisual.label}
                              color={isActiva ? cfgVisual.color : "success"}
                              sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
                            />
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="flex"
                              alignItems="center"
                              fontWeight="bold"
                            >
                              <AccessTime sx={{ fontSize: 14, mr: 0.5 }} />
                              {dayjs(inc.fecha).format("HH:mm")}
                            </Typography>
                          </Box>

                          <Typography variant="body1" fontWeight="bold" mb={2} sx={{ fontSize: "1.1rem" }}>
                            "{inc.inc_descripcion}"
                          </Typography>

                          {/* INFORMACIÓN DE QUIÉN REPORTA */}
                          <Box display="flex" alignItems="center" mb={1} p={1.5} bgcolor="#f5f5f5" borderRadius={2}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: 14, mr: 1.5, bgcolor: "#196C87" }}>
                              <Person fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" display="block" color="textSecondary" lineHeight={1}>
                                Reportado por:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {inc.del_nombre}
                              </Typography>
                            </Box>
                          </Box>

                          {/* INFORMACIÓN DEL LUGAR (Añadiendo Recinto si es Global) */}
                          {inc.junta_nombre && (
                            <Box display="flex" alignItems="flex-start" mb={2} px={1} mt={1}>
                              <LocationOn color="action" sx={{ fontSize: 18, mr: 1, mt: 0.3 }} />
                              <Box>
                                <Typography variant="body2" fontWeight="bold" color="textSecondary">
                                  Lugar: {inc.junta_nombre}
                                </Typography>
                                {esGlobal && (
                                  <Typography variant="caption" color="primary.main" fontWeight="bold">
                                    Recinto: {inc.reci_nombre}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {isActiva && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Button
                                variant="outlined"
                                color="success"
                                fullWidth
                                size="large"
                                onClick={() => handleResolverAlerta(inc.inc_codigo)}
                                startIcon={<CheckCircle />}
                                sx={{ fontWeight: "bold", borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                              >
                                MARCAR COMO RESUELTA
                              </Button>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default AlertasSeguridadRecinto
